import { useState, useRef, useCallback, type KeyboardEvent } from 'react'
import { X } from 'lucide-react'
import { cn } from '../utils/cn'
import { useTranslations } from '../providers/ContactServiceProvider'

export interface PillEmailInputProps {
  value: string[]
  onChange: (emails: string[]) => void
  disabled?: boolean
  hasError?: boolean
  id?: string
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function PillEmailInput({
  value,
  onChange,
  disabled = false,
  hasError = false,
  id,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: PillEmailInputProps) {
  const [inputValue, setInputValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const t = useTranslations()

  const addEmail = useCallback(
    (raw: string) => {
      const email = raw.trim().toLowerCase()
      if (!email) return

      if (!EMAIL_REGEX.test(email)) {
        setError(t.pillEmail.error.invalidFormat)
        return
      }

      if (value.includes(email)) {
        setError(t.pillEmail.error.duplicate)
        return
      }

      setError(null)
      setInputValue('')
      onChange([...value, email])
    },
    [value, onChange, t],
  )

  const removeEmail = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index))
    },
    [value, onChange],
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
        e.preventDefault()
        addEmail(inputValue)
      } else if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
        removeEmail(value.length - 1)
      }
    },
    [inputValue, value, addEmail, removeEmail],
  )

  const handleBlur = useCallback(() => {
    if (inputValue.trim()) {
      addEmail(inputValue)
    }
  }, [inputValue, addEmail])

  if (disabled) {
    return (
      <div
        role="listbox"
        aria-label={t.pillEmail.aria.list}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        className="flex min-h-[2.5rem] flex-wrap gap-1.5 rounded-lg border border-border-primary bg-bg-secondary px-3 py-2 text-sm text-content-tertiary cursor-not-allowed dark:border-dark-border-primary dark:bg-dark-bg-secondary dark:text-dark-content-tertiary"
        id={id}
      >
        {value.length > 0 ? (
          value.map((email, i) => (
            <span
              key={email}
              role="option"
              aria-selected
              aria-label={t.pillEmail.aria.emailLabel(email)}
              className="inline-flex items-center rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-600 dark:bg-dark-bg-custom dark:text-dark-content-custom"
            >
              {email}
            </span>
          ))
        ) : (
          <span className="text-content-tertiary dark:text-dark-content-tertiary">&mdash;</span>
        )}
      </div>
    )
  }

  return (
    <div>
      <div
        role="listbox"
        aria-label={t.pillEmail.aria.list}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        className={cn(
          'flex min-h-[2.5rem] flex-wrap items-center gap-1.5 rounded-lg border bg-bg-primary px-3 py-1.5 text-sm text-content-primary transition-all duration-150',
          'focus-within:ring-2 focus-within:ring-[#329cff] dark:focus-within:ring-[#329cff]',
          'dark:bg-dark-bg-primary dark:border-dark-border-primary dark:text-dark-content-primary',
          hasError || error
            ? 'border-red-500 focus-within:ring-red-500'
            : 'border-border-primary',
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((email, i) => (
          <span
            key={email}
            role="option"
            aria-selected
            aria-label={t.pillEmail.aria.emailLabel(email)}
            className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-600 dark:bg-dark-bg-custom dark:text-dark-content-custom"
          >
            {email}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                removeEmail(i)
              }}
              className="ml-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full hover:bg-primary-200 dark:hover:bg-dark-bg-custom/50"
              aria-label={t.pillEmail.aria.removeEmail(email)}
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="email"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value)
            if (error) setError(null)
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          maxLength={256}
          placeholder={value.length === 0 ? t.pillEmail.placeholder : ''}
          aria-label={t.pillEmail.aria.addEmail}
          className="min-w-[120px] flex-1 bg-transparent outline-hidden border-none shadow-none text-content-primary placeholder:text-content-tertiary dark:text-dark-content-primary dark:placeholder:text-dark-content-tertiary focus:outline-hidden focus:ring-0"
          id={id}
        />
      </div>
      {error && (
        <p className="mt-1 text-xs text-content-extensions-red" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
